import React, { useRef, useState, useEffect, useMemo } from "react";
import {
  VStack,
  Box,
  useToast,
  Spinner,
  Flex,
  useDisclosure,
  Button,
} from "@chakra-ui/react";
import { HeadingText } from "@root/components/common/text/HeadingText";
import { NormalText } from "@root/components/common/text/MediumText";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import {
  createDocumentWithProgress,
  getAllDocuments,
  processDocument,
  processDocumentLegal,
  processDocumentReview,
  createExtractionReviews,
  getExtractionReviews,
  callExtractionReviewsAI,
  deleteExtractionReview,
} from "@root/store/thunks/documentsActions";
import { UploadFileArea } from "./UploadFileArea";
import { DocumentList } from "./DocumentList";
import { UploadedDocument } from "./types";
import {
  ACTION,
  CHATBOT_TYPE,
  FILE_TYPE,
  ROUTE_NAMES,
  TOAST_VARIANT,
} from "@root/constants";
import { useLocation, useNavigate } from "react-router-dom";
import { LoadingSpinner } from "@root/components/analysis-loader/loader";
import { Paginate } from "@root/components/pagination";
import { useProjectsContext } from "@root/context/projects-context";
import { getChatType, getCustomFileName, getFileType } from "@root/utils";
import { formatISO } from "date-fns";
import { DataTable } from "@root/components/datatable";
import { TableColumns } from "@root/components/adminDashboard/TableColumns";
import { DocTableColumns } from "./doctablecolumns";
import { ModalDelete } from "@root/components/modal-delete";
import { useNotification } from "@root/hooks/useNotification";
import { useMainChatContext } from "@root/context/kps-chat-context";
import { ViewHistoryDrawer } from "./ViewHistoryDrawer";
import { ChatHistoryDrawer } from "@root/components/chat-history/chat-history-drawer";
import HistoryRefreshIcon from "@root/assets/icons/history-refresh-icon";
import { ArrowUpRightIcon } from "@root/assets/icons/arrowup";
import { CommonButton } from "@root/components/common/button/commonButton";
import PillToggle from "./permit-toggler";
import { TourGuide } from "@root/components/tour-guide/TourGuide";
import { RestartTourButton } from "@root/components/tour-guide/RestartTourButton";
import { TourGuideHoverTooltip } from "@root/components/tour-guide/TourGuideHoverTooltip";
import { usePermitReviewTourGuide } from "@root/hooks/usePermitReviewTourGuide";
import ModelDisplay from "@root/pages/dashboard/components/model-selection-toggler.tsx/model-text";

export const UploadDocument = () => {
  const [allDocuments, setAllDocuments] = useState<UploadedDocument[]>([]);
  const {
    setDocumentData,
    deleteDocument,
    deleteDocumentLoading,
    updateDocument,
    updateDocumentLoading,
  } = useProjectsContext();
  const [page, setPage] = useState(1);
  const [docCount, setdocCount] = useState(0);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const pageSize = 10;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const toast = useToast();
  const { t } = useTranslation("common");
  const dispatch = useDispatch();
  const { showNotification } = useNotification();
  const { pathname } = useLocation();
  const isPermitReview = pathname === ROUTE_NAMES.PERMIT_REVIEW;

  // Tour guide setup
  const {
    pillToggleRef,
    uploadFileAreaRef,
    dataTableRef,
    permitReviewTourGuide,
    permitPillToggleHover,
    permitUploadAreaHover,
    permitDataTableHover,
    permitDocumentActionsHover,
  } = usePermitReviewTourGuide({ allDocuments });

  const isLegalReview = pathname === ROUTE_NAMES.LEGAL_REVIEW;
  const isDrawingAnalysis = pathname === ROUTE_NAMES.DRAWING_ANALYSIS;
  const isMultiDoc = pathname === ROUTE_NAMES.CHECKLIST;
  const isLegalChatbot = pathname === ROUTE_NAMES.LEGAL_CHATBOT;
  const navigate = useNavigate();
  const {
    fetchChats,
    multiDocUploadView,
    setMultiDocUploadView,
    review,
    setReview,
  } = useMainChatContext();

  const [uploadingFiles, setUploadingFiles] = useState<
    {
      file: File;
      progress: number;
      id?: number;
      status?: "uploading" | "completed" | "failed";
    }[]
  >([]);
  const abortControllersRef = useRef<Record<string, AbortController>>({});
  const [highlightedFileId, setHighlightedFileId] = useState<number | null>(
    null
  );
  const currentBatchRef = useRef<Set<string>>(new Set());
  const batchStateRef = useRef<{
    fileIds: number[];
    completedCount: number;
    failedCount: number;
    totalFiles: number;
    fileNames: string[];
  } | null>(null);
  // Track API progress to pause polling
  const [isUploadInProgress, setIsUploadInProgress] = useState(false);
  const [isExtractionReviewInProgress, setIsExtractionReviewInProgress] =
    useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isHistoryOpen,
    onOpen: onHistoryOpen,
    onClose: onHistoryClose,
  } = useDisclosure();
  const {
    isOpen: isChatHistoryOpen,
    onOpen: onChatHistoryOpen,
    onClose: onChatHistoryClose,
  } = useDisclosure();
  const [selectedDeleteId, setSelectedDeleteId] = useState<number | null>(null);
  const [editingRowId, setEditingRowId] = useState<number | null>(null);
  const [editingFileId, setEditingFileId] = useState<number | null>(null);
  const [editingValue, setEditingValue] = useState<string>("");
  const [selectedPdfId, setSelectedPdfId] = useState<number | null>(null);
  const [active, setActive] = useState(FILE_TYPE.EXTRACTION_REVIEW);

  const isPermitSelected =
    pathname.startsWith(ROUTE_NAMES.PERMIT_REVIEW) &&
    review === FILE_TYPE.EXTRACTION_REVIEW;

  // Loading state for delete extraction review
  const [deleteExtractionReviewLoading, setDeleteExtractionReviewLoading] =
    useState(false);

  useEffect(() => {
    setReview(2);
  }, []);

  useEffect(() => {
    if (isPermitSelected) {
      fetchExtractionReviews(payload);
    } else {
      fetchAllDocs(payload);
    }
  }, [pathname, page, review]);

  // Separate effect to handle polling - pauses during editing or API operations
  useEffect(() => {
    let interval: NodeJS.Timeout;
    // Only start polling if user is not currently editing and no APIs are in progress
    const shouldPoll =
      !editingRowId &&
      !editingFileId &&
      !isUploadInProgress &&
      !isExtractionReviewInProgress;

    if (shouldPoll) {
      console.log(
        "Starting polling - user not editing and no APIs in progress"
      );
      interval = setInterval(() => {
        if (isPermitSelected) {
          fetchExtractionReviews(payload, true); // true = isPolling
        } else {
          fetchAllDocs(payload, true); // true = isPolling
        }
      }, 10000);
    } else {
      if (editingRowId || editingFileId) {
        console.log("⏸️ Polling paused - user is editing");
      } else if (isUploadInProgress) {
        console.log("⏸️ Polling paused - document upload in progress");
      } else if (isExtractionReviewInProgress) {
        console.log(
          "⏸️ Polling paused - extraction review creation in progress"
        );
      }
    }

    return () => {
      if (interval) {
        console.log("🛑 Stopping polling interval");
        clearInterval(interval);
      }
    };
  }, [
    editingRowId,
    editingFileId,
    isUploadInProgress,
    isExtractionReviewInProgress,
    review,
    page,
  ]);

  function handlePageChange(newPage: number) {
    setPage(newPage);
    const updatedPayload = {
      ...payload,
      page: newPage,
    };
    if (isPermitSelected) {
      fetchExtractionReviews(updatedPayload);
    } else {
      fetchAllDocs(updatedPayload);
    }
  }

  const fetchAllDocs = async (payload: any, isPolling: boolean = false) => {
    // Only show loader for user actions, not during polling
    if (!isPolling) {
      setDocumentsLoading(true);
    }
    try {
      const result = await dispatch(getAllDocuments({ payload }));
      setdocCount(result?.payload?.count);
      const rows = result?.payload?.rows ?? [];
      const docs: UploadedDocument[] = rows.map((doc: any) => ({
        id: doc.id,
        createdAt: doc.createdAt,
        status: doc?.status,
        type: doc?.extension,
        signedUrl: doc?.signedUrl,
        name: doc?.fileName_key ?? doc?.fileName ?? "",
      }));
      setAllDocuments(docs);
    } catch (error) {
      console.warn(error);
    } finally {
      if (!isPolling) {
        setDocumentsLoading(false);
      }
    }
  };

  const fetchExtractionReviews = async (
    payload: any,
    isPolling: boolean = false
  ) => {
    // Only show loader for user actions, not during polling
    if (!isPolling) {
      setDocumentsLoading(true);
    }
    try {
      const result = await dispatch(
        getExtractionReviews({
          payload: {
            page: payload.page,
            limit: payload.limit,
          },
        })
      );
      setdocCount(result?.payload?.count);
      const rows = result?.payload?.rows ?? [];
      // Transform extraction reviews: each review with its files array becomes one row
      const docs: UploadedDocument[] = rows.map((review: any) => {
        // Combine all filenames from the files array, one per line
        const fileNames =
          review.files
            ?.map((file: any) => file?.fileName_key || file?.fileName || "")
            ?.filter(Boolean)
            ?.join("\n") || "";

        // Use the first file's extension or default to pdf
        const firstFile = review?.files?.[0];
        const extension = firstFile?.extension || "pdf";

        return {
          id: review?.id,
          uploaded: review?.createdAt
            ? new Date(review?.createdAt).toLocaleString()
            : "",
          status: review?.status,
          type: extension as "pdf" | "docx",
          signedUrl: firstFile?.signedUrl,
          name: fileNames,
          // Store the files array for potential future use
          files: review?.files,
          // Store createdAt for date column
          createdAt: review?.createdAt,
        };
      });
      setAllDocuments(docs);
    } catch (error) {
      console.warn(error);
    } finally {
      if (!isPolling) {
        setDocumentsLoading(false);
      }
    }
  };

  let payload: any = {};

  if (isPermitReview) {
    payload = {
      type: review,
      limit: pageSize,
      page,
    };
  } else {
    payload = {
      type: getFileType(pathname),
      limit: pageSize,
      page,
    };
  }

  const handleFiles = (files: FileList | null) => {
    if (!files) return;

    const fileArray = Array.from(files);

    // If isPermitSelected is false, use original behavior
    if (!isPermitSelected) {
      fileArray?.forEach((file) => {
        // Initialize file in uploading state with 0 progress
        setUploadingFiles((files) => [...files, { file, progress: 0 }]);

        const formData = new FormData();
        formData.append("file", file);
        formData.append("type", String(payload.type));
        formData.append("name", file.name);

        // create and store abort controller for this file
        // If an upload with same name exists, abort it first
        const prev = abortControllersRef?.current?.[file?.name];
        if (prev) {
          try {
            prev.abort();
          } catch {}
        }
        const controller = new AbortController();
        abortControllersRef.current[file.name] = controller;

        setIsUploadInProgress(true);
        dispatch(
          createDocumentWithProgress({
            formData,
            signal: controller.signal,
            onProgress: (progress: number) => {
              setUploadingFiles((files) =>
                files.map((f) =>
                  f.file.name === file.name
                    ? { ...f, progress: Math.min(100, Math.floor(progress)) }
                    : f
                )
              );
            },
            onSuccess: (res: any) => {
              setIsUploadInProgress(false);
              showNotification({
                type: "success",
                message: t("projects.documentAddedSuccess"),
              });
              setUploadingFiles((files) =>
                files.filter((f) => f?.file?.name !== file?.name)
              );
              // cleanup controller
              delete abortControllersRef?.current?.[file?.name];
              const doc = res;
              console.log("🔄 Upload success, document:", doc);

              if (doc?.id) {
                console.log("✨ Setting highlight for file ID:", doc.id);
                // Highlight the newly uploaded file
                setHighlightedFileId(doc?.id);
              }
              if (doc?.type === FILE_TYPE.MULTI_DOC) {
                handleManualProcess(doc); // Auto start processing the file when file type is multi-doc
              }
              fetchAllDocs(payload);
            },
            onError: () => {
              setIsUploadInProgress(false);
              setUploadingFiles((files) =>
                files.filter((f) => f?.file?.name !== file?.name)
              );
              // cleanup controller
              delete abortControllersRef?.current?.[file?.name];
              toast({
                title: t("upload_document.upload_failed"),
                status: "error",
              });
            },
          })
        );
      });
      return;
    }

    // If isPermitSelected is true, use batch processing with extraction-reviews API
    // Track all files in this batch
    fileArray?.forEach((file) => {
      currentBatchRef?.current?.add(file?.name);
    });

    // Mark upload as in progress for batch
    setIsUploadInProgress(true);

    // Initialize batch state
    batchStateRef.current = {
      fileIds: [],
      completedCount: 0,
      failedCount: 0,
      totalFiles: fileArray?.length,
      fileNames: fileArray?.map((f) => f?.name),
    };

    // Initialize all files in uploading state
    setUploadingFiles((prevFiles) => [
      ...prevFiles,
      ...fileArray?.map((file) => ({
        file,
        progress: 0,
        status: "uploading" as const,
      })),
    ]);

    const checkBatchCompletion = async () => {
      const batchState = batchStateRef?.current;
      if (!batchState) return;

      // If all files in the batch are done (either completed or failed)
      if (
        batchState.completedCount + batchState?.failedCount ===
        batchState?.totalFiles
      ) {
        // Remove all files from the batch from currentBatchRef
        batchState?.fileNames?.forEach((fileName) => {
          currentBatchRef.current?.delete(fileName);
        });

        // Mark batch upload as complete
        setIsUploadInProgress(false);

        // If all files succeeded, call the extraction-reviews API
        if (batchState?.failedCount === 0 && batchState?.fileIds?.length > 0) {
          setIsExtractionReviewInProgress(true);
          dispatch(
            createExtractionReviews({
              fileIds: batchState?.fileIds,
              onSuccess: () => {
                setIsExtractionReviewInProgress(false);
                const documentCount = batchState?.fileIds?.length || 0;
                showNotification({
                  type: "success",
                  message: t(
                    documentCount === 1
                      ? "projects.documentAddedSuccess"
                      : "projects.documentsAddedSuccess"
                  ),
                });
                fetchExtractionReviews(payload);
              },
              onError: () => {
                setIsExtractionReviewInProgress(false);
                showNotification({
                  type: "success",
                  message: t("projects.documentAddFailedError"),
                });
              },
            })
          );
        }

        // Remove all files from the UI
        setUploadingFiles((prevFiles) =>
          prevFiles.filter(
            (f) => !batchState?.fileNames?.includes(f?.file?.name)
          )
        );

        // Clear batch state
        batchStateRef.current = null;

        // Refresh the document list
        if (isPermitSelected) {
          fetchExtractionReviews(payload);
        } else {
          fetchAllDocs(payload);
        }
      }
    };

    fileArray.forEach((file) => {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("type", String(payload.type));
      formData.append("name", file.name);

      // create and store abort controller for this file
      // If an upload with same name exists, abort it first
      const prev = abortControllersRef.current?.[file?.name];
      if (prev) {
        try {
          prev.abort();
        } catch {}
      }
      const controller = new AbortController();
      abortControllersRef.current[file.name] = controller;

      // Note: isUploadInProgress is set at batch start, not per file
      dispatch(
        createDocumentWithProgress({
          formData,
          signal: controller.signal,
          onProgress: (progress: number) => {
            setUploadingFiles((files) =>
              files.map((f) =>
                f.file.name === file.name
                  ? { ...f, progress: Math.min(100, Math.floor(progress)) }
                  : f
              )
            );
          },
          onSuccess: (res: any) => {
            delete abortControllersRef.current[file.name];
            const doc = res;

            // Update file status to completed
            setUploadingFiles((files) =>
              files.map((f) =>
                f.file.name === file.name
                  ? { ...f, status: "completed" as const, id: doc?.id }
                  : f
              )
            );

            // Update batch state
            if (batchStateRef.current) {
              if (doc?.id) {
                batchStateRef.current.fileIds.push(doc.id);
                console.log("✨ File uploaded with ID:", doc.id);
                // Highlight the first uploaded file
                if (batchStateRef?.current?.fileIds?.length === 1) {
                  setHighlightedFileId(doc.id);
                }
              }
              batchStateRef.current.completedCount++;
              checkBatchCompletion();
            }
          },
          onError: () => {
            // cleanup controller
            delete abortControllersRef.current[file.name];

            // Update file status to failed
            setUploadingFiles((files) =>
              files.map((f) =>
                f.file.name === file.name
                  ? { ...f, status: "failed" as const }
                  : f
              )
            );

            // Update batch state
            if (batchStateRef.current) {
              batchStateRef.current.failedCount++;
              const batchState = batchStateRef.current;

              // Only show error toast if this is the first failure and no files have completed yet
              if (
                batchState.failedCount === 1 &&
                batchState.completedCount === 0
              ) {
                toast({
                  title: t("upload_document.upload_failed"),
                  status: "error",
                });
              }

              checkBatchCompletion();
            }
          },
        })
      );
    });
  };

  const onCancelUpload = (file: File) => {
    // abort request
    const controller = abortControllersRef.current[file.name];
    if (controller) {
      try {
        controller.abort();
      } catch {}
      delete abortControllersRef.current[file.name];
    }
    // remove from UI list
    setUploadingFiles((files) =>
      files.filter((f) => f.file.name !== file.name)
    );
    // notify user
    showNotification({
      type: "success",
      message: t("upload_document.upload_canceled"),
    });
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    handleFiles(e.dataTransfer.files);
  };

  const handleManualProcess = (file?: UploadedDocument) => {
    const { id: fileId } = file ?? {};
    if (!fileId) return;
    if (
      (isPermitReview || isLegalReview || isPermitSelected) &&
      file?.status &&
      file?.status >= 1
    ) {
      if (isPermitSelected) {
        navigate(`${ROUTE_NAMES.EXTRACTION_REVIEW}/${fileId}`, {
          state: {
            isPermitSelected,
          },
        });
      } else {
        navigate(`${ROUTE_NAMES.PERMIT_REVIEW}/${fileId}`, {
          state: {
            isPermitSelected,
          },
        });
      }
    } else if (isDrawingAnalysis) {
      setDocumentData(null);
      navigate(`${ROUTE_NAMES.DRAWING_ANALYSIS_CHAT}`, {
        state: {
          file,
        },
      });
    } else if (file?.status === 0) {
      if (isPermitSelected) {
        dispatch(
          callExtractionReviewsAI({
            id: fileId,
            onSuccess: () => {
              navigate(`${ROUTE_NAMES.EXTRACTION_REVIEW}/${fileId}`, {
                state: {
                  isPermitSelected,
                },
              });
            },
            onError: () => {
              toast({
                title: t("upload_document.error_processing"),
                status: "error",
              });
            },
          })
        );
      } else if (isPermitReview && review === FILE_TYPE.EXCEL_AUTO_COMPLETE) {
        dispatch(
          processDocumentLegal({
            id: fileId,
            onSuccess: () => {
              navigate(`${ROUTE_NAMES.LEGAL_REVIEW}/${fileId}`);
            },
            onError: () => {
              toast({
                title: t("upload_document.error_processing"),
                status: "error",
              });
            },
          })
        );
      } else {
        dispatch(
          processDocument({
            id: fileId,
            onSuccess: () => {
              fetchAllDocs(payload);
            },
            onError: () => {
              toast({
                title: t("upload_document.error_processing"),
                status: "error",
              });
            },
          })
        );
      }
    }
  };

  const handleActions = (action: ACTION, row: any) => {
    if (action === ACTION.DELETE) {
      setSelectedDeleteId(row?.id ?? null);
      onOpen();
    } else if (action === ACTION.EDIT) {
      // When isPermitSelected is true and row has an id (file id), use file-level editing
      if (isPermitSelected && row?.id && row?.fileName_key !== undefined) {
        // This is a file object from the files array
        setEditingFileId(row?.id);
        setEditingValue(row?.fileName_key || row?.fileName || "");
      } else {
        // This is a document/review row
        setEditingRowId(row?.id);
        setEditingValue(row?.name || "");
      }
    }
  };

  const handleViewHistory = (row: any) => {
    setSelectedPdfId(row?.id);
    onChatHistoryOpen();
  };

  const handleSaveEdit = async (value?: string) => {
    const editingId = editingFileId || editingRowId;
    const valueToSave = value || editingValue;
    if (!editingId || !valueToSave.trim()) {
      showNotification({
        type: TOAST_VARIANT.WARNING,
        message: t("fileTable.fileNameRequired"),
      });
      return;
    }

    try {
      await updateDocument(editingId, { fileName_key: valueToSave.trim() });
      if (isPermitSelected) {
        fetchExtractionReviews(payload);
      } else {
        fetchAllDocs(payload);
      }
    } catch (error) {
      console.error("Failed to update document:", error);
    } finally {
      console.log("✅ Edit completed, resuming polling");
      setEditingRowId(null);
      setEditingFileId(null);
      setEditingValue("");
    }
  };

  const handleCancelEdit = () => {
    console.log("❌ Edit cancelled, resuming polling");
    setEditingRowId(null);
    setEditingFileId(null);
    setEditingValue("");
  };

  const handleConfirmDelete = async () => {
    if (!selectedDeleteId) {
      onClose();
      return;
    }
    try {
      if (isPermitSelected) {
        setDeleteExtractionReviewLoading(true);
        await dispatch(
          deleteExtractionReview({
            id: selectedDeleteId,
            onSuccess: () => {
              showNotification({
                type: TOAST_VARIANT.SUCCESS,
                message: t("projects.documentDeletedSuccess"),
              });
              fetchExtractionReviews(payload);
            },
            onError: () => {
              showNotification({
                type: TOAST_VARIANT.ERROR,
                message: t("projects.documentDeleteFailedError"),
              });
            },
          })
        ).unwrap();
      } else {
        await deleteDocument(selectedDeleteId);
        fetchAllDocs(payload);
      }
    } catch (e) {
      // Error handling is done in onError callbacks
    } finally {
      setDeleteExtractionReviewLoading(false);
      setSelectedDeleteId(null);
      onClose();
    }
  };

  const handleProceedClick = () => {
    // Same logic as HeadingText button
    if (isMultiDoc || isLegalChatbot) {
      setMultiDocUploadView(false);
    }
  };

  const getHeadingText = () => {
    if (isDrawingAnalysis) {
      return t("upload_document.uploadDrawing");
    }
    if (isPermitReview) {
      return t("upload_document.implementationAnalysis");
    }
    if (isLegalReview) {
      return t("upload_document.upload_legal_document");
    }
    if (isMultiDoc) {
      return t("upload_document.multiple_document_analysis");
    }
    if (isLegalChatbot) {
      return t("upload_document.upload_legal_document");
    }
    return t("upload_document.permit_document_summary_export");
  };

  const getSubHeadingText = () => {
    if (isDrawingAnalysis) {
      return t("upload_document.upload_drawing");
    }
    if (isPermitReview) {
      return t("upload_document.uploadReview");
    }
    if (isLegalReview) {
      return t("upload_document.upload_legal_files");
    }
    if (isMultiDoc) {
      return t("upload_document.upload_multi_files_analysis");
    }
    if (isLegalChatbot) {
      return t("upload_document.upload_legal_files");
    }
    return t("upload_document.upload_building_files");
  };
  const chatType = useMemo(() => {
    return getChatType(pathname);
  }, [pathname]);

  const isFileClickable = (status: number) => {
    if (isLegalReview || isPermitReview || isDrawingAnalysis || status === 0) {
      return true;
    }
    return false;
  };

  const renderDocumentsList = () => {
    if (documentsLoading) {
      return (
        <Box marginTop={"80px"} textAlign={"center"}>
          <Spinner />
        </Box>
      );
    }
    return (
      <Flex flexDir="column" w="100%" gap="8px">
        <NormalText
          fontSize={"20px"}
          fontWeight="400"
          color="#5C6670"
          text={
            isDrawingAnalysis
              ? t("upload_document.uploaded_drawings")
              : t("upload_document.uploaded_documents")
          }
        />
        <Box>
          <DataTable
            columns={DocTableColumns(
              t,
              handleActions,
              handleManualProcess,
              isFileClickable,
              editingRowId,
              editingValue,
              setEditingValue,
              handleSaveEdit,
              handleCancelEdit,
              updateDocumentLoading,
              pathname,
              handleViewHistory,
              isPermitSelected,
              editingFileId
            )}
            data={allDocuments ?? []}
            showPagination={false}
            handlePageChange={handlePageChange}
            theadBg="#F7FBFE"
          />
        </Box>

        <Flex
          style={{
            width: "100%", // 👈 key fix
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: "0.8rem",
            position: "relative",
          }}
        >
          <Box />
          <Box marginBottom={"30px"}>
            <Paginate
              page={page}
              count={docCount}
              pageSize={pageSize}
              onPageChange={handlePageChange}
            />
          </Box>
          {/* Restart Tour Button for permit review - positioned relative to pagination */}
          {isPermitReview && !permitReviewTourGuide.isActive && (
            <RestartTourButton
              onClick={permitReviewTourGuide.startTour}
              position="static"
            />
          )}
        </Flex>
      </Flex>
    );
  };
  const options = [
    {
      label: t("translate.permitReview"),
      type: FILE_TYPE.EXTRACTION_REVIEW,
      key: "extraction",
    },
    {
      label: t("translate.legalReview"),
      type: FILE_TYPE.EXCEL_AUTO_COMPLETE,
      key: "review",
    },
  ];

  return (
    <Flex minW={"100%"} flexDir="column" alignItems="center">
      <Flex
        justifyContent={isPermitReview ? "space-between" : "flex-end"}
        width="100%"
        borderBottom="2px solid #E0E0E0"
        marginBottom="20px"
        alignItems="center"
        p="10px 30px"
        gap="12px"
      >
        {isPermitReview && (
          <Box ref={pillToggleRef} {...permitPillToggleHover.hoverProps}>
            <PillToggle
              options={options}
              active={active}
              setActive={setActive}
              setState={setReview}
            />
          </Box>
        )}
        {/* Hover tooltip for permit pill toggle */}
        {isPermitReview &&
          permitPillToggleHover.isHovered &&
          permitPillToggleHover.tooltipData && (
            <TourGuideHoverTooltip
              title={permitPillToggleHover.tooltipData.title}
              description={permitPillToggleHover.tooltipData.description}
              targetRef={pillToggleRef}
              position={permitPillToggleHover.tooltipData.position}
              offset={permitPillToggleHover.tooltipData.offset}
              enabled={!permitReviewTourGuide.isActive}
              isVisible={permitPillToggleHover.isHovered}
            />
          )}
        {!isLegalChatbot && (
          <Box minW="0" flex="1 1 auto">
            <ModelDisplay modelKey={chatType} />
          </Box>
        )}

        {(isLegalChatbot || isMultiDoc) && (
          <CommonButton variant="primary" onClick={handleProceedClick}>
            {isLegalChatbot
              ? t("upload_document.proceed_legal_chatbot")
              : t("upload_document.proceed_multi_doc_analysis")}
          </CommonButton>
        )}
      </Flex>
      <Flex flexDir="column" w="100%" gap="20px" p="10px 20px">
        <Box ref={uploadFileAreaRef}>
          <UploadFileArea
            uploadingFiles={uploadingFiles}
            t={t}
            fileInputRef={fileInputRef}
            handleFiles={handleFiles}
            onDrop={onDrop}
            onCancelUpload={onCancelUpload}
            review={review}
          />
        </Box>
        <Box ref={dataTableRef}>{renderDocumentsList()}</Box>
      </Flex>
      <ModalDelete
        isOpen={isOpen}
        onClose={onClose}
        onConfirm={handleConfirmDelete}
        title={t("chat.areYouSureDelete")}
        description={t("chat.cannotBeUndone")}
        deleteButtonTitle={t("chat.deleteBtn", { defaultValue: "Delete" })}
        cancelButtonTitle={t("chat.cancelBtn", { defaultValue: "Cancel" })}
        isLoading={
          isPermitSelected
            ? deleteExtractionReviewLoading
            : deleteDocumentLoading
        }
      />

      <ChatHistoryDrawer
        isOpen={isChatHistoryOpen}
        onClose={onChatHistoryClose}
        pdf_id={selectedPdfId || undefined}
      />

      {/* Tour Guide Component */}
      {isPermitReview &&
        permitReviewTourGuide.isActive &&
        permitReviewTourGuide.currentStepData && (
          <TourGuide
            isActive={permitReviewTourGuide.isActive}
            currentStep={permitReviewTourGuide.currentStepData}
            currentStepIndex={permitReviewTourGuide.currentStep}
            totalSteps={permitReviewTourGuide.totalSteps}
            onNext={permitReviewTourGuide.nextStep}
            onBack={permitReviewTourGuide.previousStep}
            onClose={permitReviewTourGuide.stopTour}
            targetRef={
              permitReviewTourGuide.currentStepData.id === "permit-pill-toggle"
                ? pillToggleRef
                : permitReviewTourGuide.currentStepData.id ===
                  "permit-upload-area"
                ? uploadFileAreaRef
                : permitReviewTourGuide.currentStepData.id ===
                  "permit-data-table"
                ? dataTableRef
                : undefined
            }
            targetSelector={
              permitReviewTourGuide.currentStepData.id === "sidebar-permit" ||
              permitReviewTourGuide.currentStepData.id ===
                "permit-document-actions"
                ? permitReviewTourGuide.currentStepData.targetSelector
                : undefined
            }
          />
        )}
    </Flex>
  );
};
